import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTokens } from './tokens';

/**
 * SparkChart — line chart minimalista (sparkline) responsivo, com tooltip de
 * data+valor ao hover. Width 100% do container (ResponsiveContainer); height
 * controlado pelo caller.
 *
 * Sem grid/eixos/legenda — só a linha. Hover em qualquer ponto mostra um
 * activeDot + tooltip com a data formatada (DD/MM/YYYY) e o valor numérico.
 *
 * Props:
 *   values         — number[] (idx 0 = mais antigo, last = hoje)
 *   height         — px
 *   color          — cor da linha (default: T.accent)
 *   anchorDate     — Date opcional; default = hoje. Datas dos pontos são
 *                    calculadas: ponto i = anchorDate - (length-1-i) dias.
 *   fallbackLength — qtd de pontos quando values vazio/curto (linha em zero).
 */
export function SparkChart({ values, height = 48, color, anchorDate, fallbackLength = 7 }) {
    const T = useTokens();
    const c = color || T.accent;

    // Sem dados → renderiza linha em ZERO (não esconde o componente). Default
    // é 7 pontos pra "Tendência 7 dias" / Recharts da Visão Geral; caller pode
    // passar 30 pra "Últimos 30 dias". Mantém o painel visualmente igual
    // entre SKUs com e sem vendas.
    const safeValues = !values || values.length < 2 ? new Array(Math.max(2, fallbackLength)).fill(0) : values;

    const base =
        anchorDate instanceof Date
            ? new Date(anchorDate)
            : typeof anchorDate === 'string'
              ? new Date(`${anchorDate}T00:00:00Z`)
              : new Date();
    const data = safeValues.map((v, i) => {
        const d = new Date(base);
        d.setUTCDate(d.getUTCDate() - (safeValues.length - 1 - i));
        const dd = String(d.getUTCDate()).padStart(2, '0');
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        return { idx: i, value: Number(v) || 0, label: `${dd}/${mm}/${d.getUTCFullYear()}` };
    });

    const CustomTooltip = ({ active, payload }) => {
        if (!active || !payload || payload.length === 0) return null;
        const p = payload[0].payload;
        return (
            <div
                style={{
                    background: T.bg,
                    border: `1px solid ${T.line}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontFamily: T.mono,
                    color: T.ink,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    pointerEvents: 'none',
                }}
            >
                <div style={{ color: T.muted, marginBottom: 2 }}>{p.label}</div>
                <div style={{ fontWeight: 600 }}>{p.value} vendas</div>
            </div>
        );
    };

    return (
        <div data-faid="Spark_Chart_Operacoes" style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                    <Tooltip content={<CustomTooltip />} cursor={{ stroke: T.lineMid, strokeDasharray: '2 2' }} />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke={c}
                        strokeWidth={1.8}
                        dot={false}
                        activeDot={{ r: 4, fill: c, stroke: T.bg, strokeWidth: 2 }}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
